import type { IndustryDetail } from "@/types/industryDetail";
import {
  UtensilsCrossed,
  HeartPulse,
  GraduationCap,
  Boxes,
  ShoppingBag,
  Truck,
  Gauge,
  Zap,
  LineChart,
  ShieldCheck,
  Clock,
  Users,
} from "@/icons";

export const industriesA: IndustryDetail[] = [
  {
    slug: "restaurants",
    name: "Restaurants",
    sector: "Food & Beverage",
    tagline: "Digitise restaurant operations with intelligent solutions that streamline front-of-house, kitchen, inventory and customer engagement.",
    icon: UtensilsCrossed,
    accent: "#F59E0B",
    tags: ["Bookings", "POS", "Inventory", "Housekeeping", "Guest CRM"],
    image: "/images/industries/Food_Beverage.webp",
    overview:
      "Restaurants must coordinate reservations, table service, kitchen operations, online ordering, inventory, staff scheduling and customer engagement while delivering exceptional dining experiences. We develop integrated restaurant management solutions that automate daily operations, improve communication between teams, optimise inventory and provide real-time business insights across every location.",
    painPoints: [
        "Reservations, POS and online ordering operating in disconnected systems.",
        "Manual inventory tracking leading to food waste and stock shortages.",
        "Limited visibility into daily sales and operational performance.",
        "Inefficient communication between front-of-house and kitchen staff.",
        "Difficulty managing customer loyalty and repeat business.",
    ],
    manualProcesses: [
      "Taking bookings by phone into a paper diary.",
      "Counting stock by hand at close each night.",
      "Re-keying delivery-app orders into the POS.",
      "Reconciling takings across channels in spreadsheets.",
    ],
    opportunities: [
        {
            title: "Integrated Restaurant Operations",
            description: "Connect reservations, POS, kitchen operations, online ordering and inventory in one platform."
        },
        {
            title: "Workflow Automation",
            description: "Automate order processing, kitchen workflows, stock replenishment and daily operational tasks."
        },
        {
            title: "Customer Engagement",
            description: "Strengthen customer relationships through loyalty programmes, personalised promotions and mobile applications."
        },
        {
            title: "Operational Intelligence",
            description: "Use dashboards and AI-powered analytics to optimise sales, staffing and menu performance."
        },
    ],
    recommendedServices: ["web-applications", "mobile-apps", "workflow-automation", "ai-integration", "crm"],
    recommendedSolutions: ["restaurant-management", "inventory-management", "customer-portal"],
    techStack: ["React", "React Native", "Python", "FastAPI", "PostgreSQL", "Redis", "Docker", "REST APIs", "OpenAI", "n8n"],
    typicalUsers: ["Restaurant Owners", "Restaurant Managers", "Kitchen Staff", "Waiters", "Delivery Teams", "Operations Managers"],
    workflow: [
      { label: "Order", description: "Orders from any channel flow into one system and to the kitchen instantly." },
      { label: "Prepare", description: "The kitchen display sequences tickets to keep service on time." },
      { label: "Serve", description: "Tables and delivery are tracked through to completion." },
      { label: "Restock", description: "Ingredient usage updates stock and flags what to reorder." },
      { label: "Report", description: "Owners see sales, margins and trends across every outlet." },
    ],
    benefits: [
        {
            title: "Improve Customer Experience",
            description: "Deliver faster service, online ordering, reservations and personalised customer engagement across every dining channel.",
            icon: Users,
        },
        {
            title: "Increase Operational Efficiency",
            description: "Automate restaurant workflows to improve coordination between front-of-house, kitchen and delivery teams.",
            icon: Zap,
        },
        {
            title: "Reduce Food Waste",
            description: "Real-time inventory management and purchasing insights help reduce stock shortages and unnecessary waste.",
            icon: ShieldCheck,
        },
        {
            title: "Make Better Business Decisions",
            description: "AI-assisted dashboards provide visibility into sales, menu performance, staffing and customer behaviour.",
            icon: LineChart,
        },
    ],
    outcomes: [
        {label: "Reduction in Food Waste", value: "25%"},
        {label: "Increase in Table Turnover", value: "20%"},
        {label: "Increase in Customer Retention", value: "18%"},
    ],
    faqs: [
        {
            question: "Can your solutions integrate with our existing POS system?",
            answer: "Yes. We develop restaurant platforms that integrate with Point of Sale (POS) systems, payment gateways, online ordering platforms, delivery services and accounting software using secure APIs."
        },
        {
            question: "Can online ordering and table reservations be managed from one platform?",
            answer: "Yes. Our solutions integrate reservations, dine-in operations, takeaway orders and online deliveries into a single platform, giving staff complete operational visibility."
        },
        {
            question: "Can inventory and ingredient usage be tracked automatically?",
            answer: "Yes. Inventory management features monitor ingredient consumption, stock levels, supplier purchases and reorder points to help reduce food waste and improve purchasing decisions."
        },
        {
            question: "Can loyalty programmes and customer rewards be included?",
            answer: "Yes. We develop customer loyalty programmes, digital memberships, promotional campaigns and personalised offers that encourage repeat visits and strengthen customer relationships."
        },
        {
            question: "Can AI improve restaurant operations?",
            answer: "Yes. AI can assist with sales forecasting, menu performance analysis, operational reporting, customer insights and workflow automation, helping restaurant managers make faster and more informed decisions."
        },
        {
            question: "Can the platform support multiple restaurant branches?",
            answer: "Yes. Our solutions support multi-branch restaurant operations with centralised reporting while allowing each location to manage its own staff, inventory and daily operations."
        },
        {
            question: "Can mobile applications be developed for customers and restaurant staff?",
            answer: "Yes. We build mobile applications for customers, delivery personnel and restaurant staff, supporting online ordering, reservations, order tracking, inventory updates and operational management."
        },
        {
            question: "Can implementation be completed in phases?",
            answer: "Absolutely. Restaurants often begin with POS integration, online ordering or inventory management before expanding into CRM, loyalty programmes, AI capabilities and workflow automation."
        },
    ],
    caseConcept: {
        title: "Restaurant Operations Platform",
        category: "Example Project",
        summary:
            "A growing restaurant chain relied on separate systems for reservations, POS, inventory, online ordering and customer management. We implemented an integrated restaurant operations platform that streamlined daily workflows, improved customer engagement and provided real-time operational dashboards across all branches.",
        metrics: [
            {
                label: "Reduction in Food Waste",
                value: "25%",
            },
            {
                label: "Increase in Table Turnover",
                value: "20%",
            },
            {
                label: "Restaurant Branches",
                value: "14",
            },
        ],
        tech: [
            "React",
            "React Native",
            "FastAPI",
            "PostgreSQL",
            "OpenAI",
            "n8n"
        ],
        accent: "#F59E0B",
    },
  },
  {
    slug: "healthcare",
    name: "Healthcare",
    sector: "Health & Life Sciences",
    tagline: "Digitise healthcare operations with secure, intelligent solutions that improve patient care, streamline clinical workflows and simplify hospital management.",
    icon: HeartPulse,
    accent: "#0891B2",
    overview:
      "Healthcare organisations must coordinate patient care, appointments, diagnostics, billing, compliance, medical records and asset management while protecting highly sensitive information. Many providers still rely on disconnected systems, manual paperwork and inefficient workflows that increase administrative effort and delay patient care. We develop secure digital healthcare solutions that automate clinical and administrative processes, improve operational visibility and help healthcare professionals spend more time with patients instead of paperwork.",
    tags:["Hospitals", "Laboratories", "Clinics",  "Telemedicine"],
    image: "/images/industries/Health_Life_Sciences.webp",
      painPoints: [
      "Patient records spread across multiple disconnected systems.",
      "Manual appointment scheduling and administrative processes.",
      "Limited visibility into hospital operations and resource utilisation.",
      "Complex insurance, billing and compliance requirements.",
      "Medical equipment and healthcare assets difficult to monitor and maintain.",
    ],
    manualProcesses: [
      "Registering patients on paper forms.",
      "Booking appointments by phone across departments.",
      "Typing up claims for insurers by hand.",
      "Tracking equipment and stock on spreadsheets.",
    ],
    opportunities: [
      {
          title: "Digital Patient Management",
          description: "Provide secure access to patient information, appointments and clinical records across departments."
      },
      {
          title: "Workflow Automation",
          description: "Automate appointments, approvals, billing and administrative healthcare processes."
      },
      {
          title: "Healthcare Asset Management",
          description: "Track medical equipment, preventive maintenance and asset utilisation in real time."
      },
      {
          title: "Operational Intelligence",
          description: "Use dashboards and AI-powered analytics to improve operational efficiency and decision-making."
      },
    ],
    recommendedServices: ["web-applications", "mobile-apps", "workflow-automation", "ai-integration"],
    recommendedSolutions: ["hospital-management", "visitor-management", "asset-management"],
    techStack: ["React", "React Native", "Python", "FastAPI", "PostgreSQL", "Redis", "Docker", "REST APIs", "OpenAI", "n8n"],
    typicalUsers: ["Doctors", "Nurses", "Reception Staff", "Laboratory Teams", "Hospital Administrators", "Healthcare Executives"],
    workflow: [
      { label: "Register", description: "Patients are registered with secure, unified records." },
      { label: "Schedule", description: "Appointments are booked around staff and resource availability." },
      { label: "Treat", description: "Clinicians access history and update records at the point of care." },
      { label: "Bill", description: "Charges and insurance claims are generated automatically." },
      { label: "Follow up", description: "Reminders and reports support ongoing care and compliance." },
    ],
    benefits: [
      {
          title: "Improve Patient Experience",
          description: "Streamlined appointments, digital records and automated communication reduce waiting times and improve patient satisfaction.",
          icon: Clock,
      },
      {
          title: "Increase Operational Efficiency",
          description: "Workflow automation reduces administrative effort and allows healthcare professionals to focus more on patient care.",
          icon: Zap,
      },
      {
          title: "Strengthen Security & Compliance",
          description: "Role-based access, audit trails and secure architecture help organisations protect sensitive healthcare information.",
          icon: ShieldCheck,
      },
      {
          title: "Enable Better Decision Making",
          description: "Real-time dashboards and AI-driven insights improve hospital operations, resource planning and executive reporting.",
          icon: LineChart,
      },
    ],
    outcomes: [
      {label: "Reduction in Patient Waiting Time", value: "30%"},
      {label: "Faster Insurance Claim Processing", value: "45%"},
      {label: "Reduction in Missed Appointments", value: "25%"},
    ],
    faqs: [
      {
          question: "Can your solutions integrate with our existing Hospital Information System (HIS) or Electronic Medical Record (EMR) platform?",
          answer: "Yes. We design healthcare solutions that integrate securely with existing Hospital Information Systems, Electronic Medical Records, laboratory systems, billing platforms and other healthcare applications using APIs and industry-standard integration methods."
      },
      {
          question: "How do you protect sensitive patient information?",
          answer: "Security is built into every solution we develop. We implement role-based access control, encryption, secure authentication, audit trails and other best practices to help healthcare organisations protect sensitive patient data and comply with applicable regulations."
      },
      {
          question: "Can appointments and patient registration be automated?",
          answer: "Yes. We automate patient registration, appointment scheduling, reminders and administrative workflows to reduce manual effort, minimise errors and improve the overall patient experience."
      },
      {
          question: "Can your software manage hospital assets and medical equipment?",
          answer: "Yes. We develop healthcare asset management solutions that monitor medical equipment, maintenance schedules, calibration records and utilisation, helping organisations maximise equipment availability and compliance."
      },
      {
          question: "Can AI improve healthcare operations?",
          answer: "Yes. AI can assist with document processing, operational reporting, workflow automation, clinical decision support and intelligent data analysis. We focus on implementing AI where it delivers measurable operational value while respecting privacy and governance requirements."
      },
      {
          question: "Can the solution support multiple hospitals or healthcare facilities?",
          answer: "Yes. Our solutions support multi-site healthcare organisations with centralised reporting while allowing individual hospitals, clinics or departments to operate independently."
      },
      {
          question: "Can mobile applications be developed for doctors, nurses and patients?",
          answer: "Yes. We develop secure mobile applications that allow healthcare professionals and patients to access relevant information, manage appointments, receive notifications and perform authorised tasks from anywhere."
      },
      {
          question: "Can we implement the solution in phases?",
          answer: "Absolutely. Many healthcare organisations begin with a specific module such as patient management, appointments or asset management before expanding into additional clinical and administrative functions."
      },
    ],
    caseConcept: {
      title: "Healthcare Operations Platform",
      category: "Example Project",
      summary:
          "A multi-speciality healthcare organisation relied on disconnected systems for patient registration, appointments, billing, medical equipment management and reporting. We implemented an integrated healthcare platform that streamlined administrative workflows, improved patient experience and provided real-time operational dashboards for hospital management.",
      metrics: [
        {
            label: "Reduction in Patient Waiting Time",
            value: "30%",
        },
        {
            label: "Faster Insurance Claim Processing",
            value: "45%",
        },
        {
            label: "Healthcare Facilities",
            value: "8",
        },
      ],
      tech: [
          "React",
          "FastAPI",
          "PostgreSQL",
          "Docker",
      ],
      accent: "#0891B2",
    },
  },
  {
    slug: "education",
    name: "Education",
    sector: "Learning & Academia",
    tagline: "Digitise educational institutions with secure, intelligent solutions that simplify administration, improve learning experiences and strengthen communication between educators, students and parents.",
    icon: GraduationCap,
    accent: "#4F46E5",
    tags:["Schools", "Colleges", "LMS", "Admissions"],
    image: "/images/industries/Learning_Academia.webp",
    overview:
      "Educational institutions manage admissions, academics, examinations, finance, staff, student services and communication while delivering high-quality learning experiences. Many schools, colleges and universities continue to depend on disconnected software, spreadsheets and manual administration that increase workload and reduce operational efficiency. We develop integrated digital education platforms that automate administrative processes, improve collaboration, enhance student engagement and provide real-time visibility across academic and operational activities.",
    painPoints: [
      "Admissions, academics and administration managed through disconnected systems.",
      "Manual fee collection, attendance tracking and examination processes.",
      "Limited communication between teachers, students and parents.",
      "Administrative staff overwhelmed by paperwork and repetitive tasks.",
      "Difficulty accessing accurate academic and operational reports.",
    ],
    manualProcesses: [
      "Processing admissions on paper forms.",
      "Marking attendance in registers.",
      "Chasing fees by phone and email.",
      "Compiling results and reports by hand.",
    ],
    opportunities: [
      {
          title: "Digital Student Lifecycle",
          description: "Manage admissions, academics, attendance, examinations and graduation through one integrated platform."
      },
      {
          title: "Workflow Automation",
          description: "Automate admissions, approvals, fee collection, communication and administrative workflows."
      },
      {
          title: "Student & Parent Engagement",
          description: "Provide secure portals and mobile applications that improve communication and transparency."
      },
      {
          title: "Operational Intelligence",
          description: "Use dashboards and AI-powered analytics to improve academic planning, administration and institutional decision-making."
      },
    ],
    recommendedServices: ["web-applications", "mobile-apps", "workflow-automation", "ai-integration", "cloud-deployment"],
    recommendedSolutions: ["school-erp", "hrms", "customer-portal"],
    techStack: ["React", "React Native", "Python", "FastAPI", "PostgreSQL", "Redis", "Docker", "REST APIs", "OpenAI", "n8n"],
    typicalUsers: ["Administrative Staff", "Teachers", "Parents", "Students", "School Management", "Institution Executives"],
    workflow: [
      { label: "Enrol", description: "Applicants move from enquiry to enrolment in a guided flow." },
      { label: "Schedule", description: "Timetables are generated for classes, staff and rooms." },
      { label: "Teach", description: "Attendance and grades are recorded through the term." },
      { label: "Bill", description: "Fees are invoiced with automatic reminders and receipts." },
      { label: "Report", description: "Progress and finance reports reach parents and leadership." },
    ],
    benefits: [
      {
          title: "Reduce Administrative Work",
          description: "Automate admissions, attendance, examinations and fee management, allowing staff to focus more on education than paperwork.",
          icon: Clock,
      },
      {
          title: "Improve Student Experience",
          description: "Digital portals, mobile applications and automated communication keep students and parents informed throughout the academic journey.",
          icon: Users,
      },
      {
          title: "Increase Operational Efficiency",
          description: "Integrated systems eliminate duplicate data entry and improve collaboration across departments.",
          icon: Zap,
      },
      {
          title: "Support Better Institutional Decisions",
          description: "Real-time dashboards and AI-assisted reporting provide valuable insights into academic performance, finance and operations.",
          icon: LineChart,
      },
    ],
    outcomes: [
      {label: "Reduction in Administrative Work", value: "50%"},
      {label: "Increase in On-Time Fee Collection", value: "35%"},
      {label: "Parent Portal Adoption", value: "80%"},
    ],
    faqs: [
      {
          question: "Can your platform manage the complete student lifecycle?",
          answer: "Yes. Our education solutions support admissions, student registration, attendance, examinations, grading, fee management, communication and graduation within a single integrated platform."
      },
      {
          question: "Can the system support multiple schools or campuses?",
          answer: "Yes. We develop multi-campus solutions that allow institutions to manage several schools or campuses from a central platform while maintaining independent academic and administrative operations."
      },
      {
          question: "Can parents and students access information through mobile applications?",
          answer: "Yes. We develop secure mobile applications and portals that allow parents and students to view attendance, academic performance, examination results, fee status, notices and institutional announcements."
      },
      {
          question: "Can admissions and fee collection be automated?",
          answer: "Yes. Our solutions automate admissions, application processing, fee invoicing, online payments, reminders and receipt generation, reducing manual administrative work."
      },
      {
          question: "Can AI improve educational administration?",
          answer: "Yes. AI can assist with document processing, administrative automation, reporting, knowledge search, student support and operational analytics, helping institutions improve efficiency while reducing repetitive work."
      },
      {
          question: "Can the platform integrate with Learning Management Systems (LMS) and other educational software?",
          answer: "Yes. We can integrate with Learning Management Systems, online learning platforms, payment gateways, communication services and other third-party educational applications through secure APIs."
      },
      {
          question: "How secure is student and institutional data?",
          answer: "We implement secure authentication, role-based access control, encryption, audit trails and industry best practices to help educational institutions protect sensitive student and organisational information."
      },
      {
          question: "Can implementation be completed in phases?",
          answer: "Absolutely. Many institutions begin with admissions, student management or fee management before expanding into examinations, HR, finance, asset management and additional digital services."
      },
    ],
    caseConcept: {
      title: "Education Management Platform",
      category: "Example Project",
      summary:
          "A multi-campus educational institution relied on disconnected systems for admissions, academics, examinations, finance and communication. We implemented an integrated education management platform that streamlined administrative processes, improved collaboration between educators and parents and provided real-time reporting for institutional leadership.",
      metrics: [
          {
              label: "Reduction in Administrative Work",
              value: "50%",
          },
          {
              label: "Improved Fee Collection",
              value: "35%",
          },
          {
              label: "Campuses",
              value: "5",
          },
      ],
      tech: [
          "React",
          "React Native",
          "FastAPI",
          "PostgreSQL",
          "OpenAI",
          "n8n"
      ],
      accent: "#4F46E5",
    },
  },
  {
    slug: "manufacturing",
    name: "Manufacturing",
    sector: "Industrial",
    tagline: "Digitise manufacturing operations, automate workflows and gain real-time visibility across production, inventory, quality and maintenance.",
    icon: Boxes,
    accent: "#0F766E",
    tags:["ERP", "MES", "Quality", "Maintenance"],
    image: "/images/industries/Manufacturing.webp",
    overview:
      "Manufacturing organisations must coordinate production planning, inventory, procurement, quality control, maintenance and logistics while responding quickly to changing customer demand. We develop integrated digital solutions that connect manufacturing operations, automate workflows, improve decision-making and provide real-time insights across the entire production lifecycle.",
    painPoints: [
      "Production planning disconnected from inventory and procurement.",
      "Limited real-time visibility into manufacturing operations.",
      "Unplanned equipment downtime reducing productivity.",
      "Manual quality inspections and paper-based records.",
      "Disconnected systems requiring duplicate data entry.",
    ],
    manualProcesses: [
      "Planning production in spreadsheets.",
      "Logging machine maintenance on paper.",
      "Recording quality checks by hand.",
      "Counting raw materials manually.",
    ],
    opportunities: [
      {
          title: "Production Optimisation",
          description: "Improve planning by connecting production schedules with inventory, procurement and customer demand."
      },
      {
          title: "Workflow Automation",
          description: "Automate approvals, production processes and operational workflows to reduce manual effort."
      },
      {
          title: "Quality Management",
          description: "Digitise inspections, compliance and quality assurance processes across manufacturing operations."
      },
      {
          title: "Operational Intelligence",
          description: "Use dashboards, analytics and AI-powered insights to improve productivity and decision-making."
      },
    ],
    recommendedServices: ["erp-development", "workflow-automation", "ai-integration", "api-development", "cloud-deployment"],
    recommendedSolutions: ["erp", "inventory-management", "asset-management"],
    techStack: ["React", "React Native", "Python", "FastAPI", "PostgreSQL", "Redis", "Docker", "REST APIs", "OpenAI", "n8n"],
    typicalUsers: ["Production Managers", "Plant Managers", "Maintenance Engineers", "Quality Engineers", "Warehouse Teams", "Operations Executives"],
    workflow: [
      { label: "Plan", description: "Production is scheduled against capacity, stock and orders." },
      { label: "Produce", description: "Work orders track output and material consumption live." },
      { label: "Inspect", description: "Quality checks are recorded and failures flagged instantly." },
      { label: "Maintain", description: "Preventive maintenance is scheduled to avoid downtime." },
      { label: "Analyse", description: "Dashboards reveal cost, output and bottlenecks in real time." },
    ],
    benefits: [
      {
          title: "Increase Production Efficiency",
          description: "Automate manufacturing workflows and improve coordination across production, inventory and procurement.",
          icon: Zap,
      },
      {
          title: "Reduce Operational Downtime",
          description: "Proactive maintenance and better operational visibility help minimise production interruptions.",
          icon: Clock,
      },
      {
          title: "Improve Product Quality",
          description: "Digital quality management reduces defects while supporting compliance and traceability.",
          icon: ShieldCheck,
      },
      {
          title: "Make Better Business Decisions",
          description: "Real-time dashboards and AI-driven insights provide complete visibility across manufacturing operations.",
          icon: LineChart,
      },
    ],
    outcomes: [
      {label: "Reduction in Downtime", value: "35%"},
      {label: "Reduction in Defects", value: "28%"},
      {label: "Increase in On-Time Production", value: "22%"},
    ],
    faqs: [
      {
          question: "Can your solutions integrate with our existing ERP or manufacturing systems?",
          answer: "Yes. We design solutions that integrate with existing ERP systems, production equipment, inventory platforms, warehouse management systems and other business applications through secure APIs and integration services. This allows manufacturers to modernise operations without replacing every existing system."
      },
      {
          question: "Can your software support production planning and scheduling?",
          answer: "Yes. We develop production planning solutions that consider manufacturing capacity, inventory availability, work orders and customer demand. This helps production teams optimise schedules, reduce delays and improve on-time delivery."
      },
      {
          question: "Can equipment maintenance be automated?",
          answer: "Yes. Our solutions support preventive and scheduled maintenance by automatically generating maintenance tasks, sending reminders, tracking service history and providing dashboards that help reduce unexpected equipment downtime."
      },
      {
          question: "Can the system manage inventory, raw materials and work-in-progress?",
          answer: "Yes. We build inventory management solutions that provide real-time visibility into raw materials, work-in-progress and finished goods. Automated stock updates and reporting help improve inventory accuracy while reducing shortages and excess stock."
      },
      {
          question: "Can quality inspections and compliance processes be digitised?",
          answer: "Yes. Digital quality management allows manufacturers to record inspections, manage non-conformities, maintain audit trails and generate compliance reports while reducing paperwork and improving product quality."
      },
      {
          question: "Can AI improve manufacturing operations?",
          answer: "Yes. AI can analyse production data, identify operational trends, assist with predictive maintenance, generate management insights and automate repetitive business processes. We implement AI where it delivers measurable operational value rather than unnecessary complexity."
      },
      {
          question: "Can the solution support multiple factories or production sites?",
          answer: "Yes. Our applications are designed to support multi-site manufacturing operations with centralised reporting while allowing each plant to manage its own production, inventory and operational processes independently."
      },
      {
          question: "Can we start with one module and expand later?",
          answer: "Absolutely. We usually recommend a phased implementation approach. Organisations often begin with a single module such as Production Planning, Inventory Management or Asset Management and then expand to additional modules as business requirements evolve."
      },
    ],
    caseConcept: {
      title: "Manufacturing Operations Platform",
      category: "Example Project",
      summary:
          "A manufacturing organisation relied on spreadsheets and disconnected systems for production planning, inventory management, quality assurance and equipment maintenance. We implemented an integrated platform that connected manufacturing operations, automated workflows and provided real-time dashboards for production, maintenance and executive reporting.",
      metrics: [
        {
            label: "Reduction in Downtime",
            value: "35%",
        },
        {
            label: "Improved Production Visibility",
            value: "100%",
        },
        {
            label: "Reduction in Manual Reporting",
            value: "70%",
        },
      ],
      tech: [
          "React",
          "FastAPI",
          "PostgreSQL",
          "Docker",
      ],
      accent: "#0F766E",
    },
  },
  {
    slug: "retail",
    name: "Retail & E-commerce",
    sector: "Commerce",
    tagline: "Deliver seamless omnichannel retail experiences with intelligent digital solutions that connect customers, stores, inventory and operations.",
    icon: ShoppingBag,
    accent: "#ec631a",
    tags: ["POS", "Inventory", "E-Commerce", "CRM", "Analytics"],
    image: "/images/industries/E-commerce.webp",
    overview:
      "Retail businesses must manage physical stores, e-commerce platforms, inventory, customer relationships, fulfilment and supplier operations while meeting constantly changing customer expectations. We develop integrated retail solutions that automate operations, improve customer engagement, optimise inventory and provide real-time business intelligence across every sales channel.",
    painPoints: [
      "Inventory information inconsistent across online and physical stores.",
      "Disconnected customer, sales and inventory systems.",
      "Manual order processing and fulfilment workflows.",
      "Limited visibility into customer behaviour and sales performance.",
      "Difficulty scaling operations during seasonal demand.",
    ],
    manualProcesses: [
      "Counting stock by hand across locations.",
      "Updating prices channel by channel.",
      "Exporting orders between systems.",
      "Tracking loyalty in spreadsheets.",
    ],
    opportunities: [
      {
          title: "Omnichannel Retail",
          description: "Connect online stores, physical outlets and inventory into one integrated retail platform."
      },
      {
          title: "Workflow Automation",
          description: "Automate order processing, inventory updates, approvals and fulfilment workflows."
      },
      {
          title: "Customer Engagement",
          description: "Strengthen customer relationships through personalised experiences, loyalty programmes and CRM integration."
      },
      {
          title: "Operational Intelligence",
          description: "Use dashboards and AI-powered analytics to optimise sales, inventory and business performance."
      },
    ],
    recommendedServices: ["web-applications", "mobile-apps", "ai-integration", "workflow-automation", "api-development", "crm"],
    recommendedSolutions: ["inventory-management", "crm", "customer-portal"],
    techStack: ["React", "React Native", "FastAPI", "PostgreSQL", "OpenAI", "n8n", "Redis", "REST APIs"],
    typicalUsers: ["Store Managers", "Sales Associates", "Warehouse Teams", "Customer Service Teams", "Marketing Teams", "Retail Executives"],
    workflow: [
      { label: "Browse", description: "Shoppers move seamlessly between store, web and app." },
      { label: "Buy", description: "Purchases update one shared, real-time stock ledger." },
      { label: "Fulfil", description: "Orders route to the fastest store or warehouse." },
      { label: "Replenish", description: "Reorder points trigger purchasing before stockouts." },
      { label: "Retain", description: "Loyalty and personalised offers bring shoppers back." },
    ],
    benefits: [
      {
          title: "Deliver Better Customer Experiences",
          description: "Provide consistent shopping experiences across online stores, mobile applications and physical retail locations.",
          icon: Users,
      },
      {
          title: "Increase Operational Efficiency",
          description: "Automate inventory, order fulfilment and operational workflows to reduce manual effort.",
          icon: Zap,
      },
      {
          title: "Improve Inventory Accuracy",
          description: "Real-time inventory visibility helps reduce stock shortages, overstocking and fulfilment errors.",
          icon: ShieldCheck,
      },
      {
          title: "Grow Revenue Through Better Insights",
          description: "AI-assisted analytics and business dashboards help retailers identify trends, optimise pricing and improve sales performance.",
          icon: LineChart,
      },
    ],
    outcomes: [
      {
          label: "Reduction in Order Processing Time",
          value: "45%",
      },
      {
          label: "Increase in Inventory Accuracy",
          value: "30%",
      },
      {
          label: "Increase in Customer Retention",
          value: "20%",
      },
    ],
    faqs: [
      {
          question: "Can your solutions integrate with our existing POS, ERP or e-commerce platform?",
          answer: "Yes. We develop retail solutions that integrate with Point of Sale (POS) systems, ERP software, e-commerce platforms, payment gateways and third-party logistics providers through secure APIs."
      },
      {
          question: "Can inventory be synchronised across multiple stores and online channels?",
          answer: "Yes. Our solutions provide real-time inventory synchronisation across physical stores, warehouses and e-commerce platforms, helping retailers maintain accurate stock information and reduce fulfilment errors."
      },
      {
          question: "Can customer loyalty programmes be included?",
          answer: "Yes. We develop loyalty programmes, rewards systems, personalised offers and customer engagement features that encourage repeat purchases and improve customer retention."
      },
      {
          question: "Can AI improve retail operations?",
          answer: "Yes. AI can assist with sales forecasting, customer behaviour analysis, intelligent search, personalised recommendations, operational reporting and workflow automation, helping retailers make faster and more informed business decisions."
      },
      {
          question: "Can mobile applications be developed for customers and store staff?",
          answer: "Yes. We build secure mobile applications that support customer shopping experiences as well as internal applications for inventory management, stock transfers, order fulfilment and store operations."
      },
      {
          question: "Can your platform support multiple stores or franchises?",
          answer: "Yes. Our solutions support multi-store and franchise operations with centralised reporting while allowing individual locations to manage their own inventory, sales and daily operations."
      },
      {
          question: "Can the solution scale as our business grows?",
          answer: "Absolutely. Our retail platforms are designed with scalability in mind, allowing businesses to add stores, warehouses, users and new digital services without requiring a complete system redesign."
      },
      {
          question: "Can implementation be phased?",
          answer: "Yes. Many retailers begin with inventory management, e-commerce integration or CRM before expanding into loyalty programmes, AI capabilities and broader workflow automation."
      },
    ],
    caseConcept: {
      title: "Omnichannel Retail Platform",
      category: "Example Project",
      summary:
          "A growing retail business operated separate systems for e-commerce, physical stores, inventory and customer management. We developed an integrated omnichannel retail platform that unified operations, automated order processing and provided real-time business dashboards for management.",
      metrics: [
        {
            label: "Reduction in Order Processing Time",
            value: "45%",
        },
        {
            label: "Increase in Inventory Accuracy",
            value: "30%",
        },
        {
            label: "Retail Locations",
            value: "18",
        },
      ],
      tech: [
          "React",
          "React Native",
          "FastAPI",
          "PostgreSQL",
          "n8n",
      ],
      accent: "#ec631a",
    },
  },
  {
    slug: "logistics",
    name: "Logistics & Supply Chain",
    sector: "Transport & Distribution",
    tagline: "Optimise logistics and supply chain operations with intelligent digital solutions that improve visibility, automate workflows and streamline transportation, warehousing and delivery.",
    icon: Truck,
    accent: "#0891B2",
    tags: ["Fleet", "Warehouse", "GPS", "Delivery", "Tracking"],
    image: "/images/industries/Logistics.webp",
    overview:
      "Logistics organisations coordinate transportation, warehousing, fleet operations, inventory, deliveries and customer communication while responding to constantly changing operational demands. We develop integrated logistics solutions that automate workflows, improve shipment visibility, optimise resource utilisation and provide real-time operational intelligence across the supply chain.",
    painPoints: [
      "Limited real-time visibility into shipments and fleet operations.",
      "Manual dispatching, scheduling and delivery coordination.",
      "Disconnected warehouse, inventory and transportation systems.",
      "Difficulty monitoring vehicle utilisation and delivery performance.",
      "Manual reporting and limited operational analytics.",
    ],
    manualProcesses: [
      "Planning routes on maps and spreadsheets.",
      "Calling drivers for status updates.",
      "Recording proof-of-delivery on paper.",
      "Reconciling carrier invoices by hand.",
    ],
    opportunities: [
      {
          title: "Fleet & Transportation Management",
          description: "Digitise fleet operations, dispatching and delivery management with real-time tracking."
      },
      {
          title: "Workflow Automation",
          description: "Automate shipment processing, approvals, notifications and operational workflows."
      },
      {
          title: "Warehouse & Inventory Visibility",
          description: "Connect warehouse operations with transportation and inventory systems for end-to-end visibility."
      },
      {
          title: "Operational Intelligence",
          description: "Use dashboards and AI-powered analytics to optimise routes, resources and supply chain performance."
      },
    ],
    recommendedServices: ["mobile-apps", "workflow-automation", "api-development", "ai-integration", "cloud-deployment"],
    recommendedSolutions: ["inventory-management", "asset-management", "erp"],
    techStack: ["React", "React Native", "Python", "FastAPI", "PostgreSQL", "Redis", "Docker", "REST APIs", "OpenAI", "n8n", "GPS & Maps APIs"],
    typicalUsers: ["Warehouse Managers", "Fleet Managers", "Dispatch Coordinators", "Drivers", "Operations Managers", "Supply Chain Executives"],
    workflow: [
      { label: "Receive", description: "Orders and inbound stock are logged against live inventory." },
      { label: "Plan", description: "Dispatch and routes are optimised for cost and time." },
      { label: "Ship", description: "Shipments are tracked in real time with status updates." },
      { label: "Deliver", description: "Digital proof-of-delivery closes the loop instantly." },
      { label: "Report", description: "Dashboards reveal performance, cost and exceptions." },
    ],
    benefits: [
      {
          title: "Improve Operational Visibility",
          description: "Track shipments, fleet operations and warehouse activities in real time through integrated dashboards.",
          icon: LineChart,
      },
      {
          title: "Increase Delivery Efficiency",
          description: "Automated dispatching, route optimisation and workflow automation improve delivery performance.",
          icon: Zap,
      },
      {
          title: "Reduce Operational Costs",
          description: "Optimised transportation planning and better resource utilisation help lower logistics expenses.",
          icon: ShieldCheck,
      },
      {
          title: "Enhance Customer Experience",
          description: "Provide customers with shipment tracking, automated notifications and improved delivery transparency.",
          icon: Users,
      },
    ],
    outcomes: [
      {label: "Reduction in Delivery Delays", value: "30%"},
      {label: "Increase in Fleet Utilisation", value: "25%"},
      {label: "Reduction in Manual Dispatch Work", value: "50%"},
    ],
    faqs: [
      {
          question: "Can your solutions integrate with our existing warehouse, ERP or transportation management systems?",
          answer: "Yes. We develop logistics platforms that integrate with warehouse management systems, ERP software, transportation management systems, GPS platforms and third-party logistics providers using secure APIs."
      },
      {
          question: "Can drivers use mobile applications?",
          answer: "Yes. We build mobile applications that allow drivers to receive assignments, update delivery status, capture proof of delivery, upload photographs, collect digital signatures and communicate with dispatch teams."
      },
      {
          question: "Can shipments be tracked in real time?",
          answer: "Yes. Our solutions support real-time shipment tracking through GPS integration, automated status updates and operational dashboards that provide visibility across the supply chain."
      },
      {
          question: "Can warehouse operations be integrated with transportation management?",
          answer: "Yes. We develop integrated solutions that connect warehouse operations, inventory, dispatching and transportation management, providing end-to-end operational visibility."
      },
      {
          question: "Can AI improve logistics operations?",
          answer: "Yes. AI can assist with route optimisation, operational reporting, demand forecasting, document processing and workflow automation. We implement AI where it improves efficiency and supports better operational decisions."
      },
      {
          question: "Can the platform support multiple warehouses and distribution centres?",
          answer: "Yes. Our logistics solutions support multiple warehouses, regional distribution centres and transportation hubs while providing centralised reporting and operational control."
      },
      {
          question: "Can customers receive automated shipment notifications?",
          answer: "Yes. Automated notifications can inform customers about shipment confirmations, dispatch, estimated delivery times and successful deliveries through email, SMS or mobile applications."
      },
      {
          question: "Can implementation be phased?",
          answer: "Absolutely. Organisations often begin with fleet management, warehouse operations or shipment tracking before expanding into broader logistics automation and supply chain management."
      },
    ],
    caseConcept: {
      title: "Logistics Operations Platform",
      category: "Example Project",
      summary:
          "A regional logistics provider relied on disconnected systems for fleet management, warehouse operations, dispatching and shipment tracking. We implemented an integrated logistics platform that automated operational workflows, improved shipment visibility and provided real-time dashboards for transportation and warehouse management.",
      metrics: [
          {
              label: "Reduction in Delivery Delays",
              value: "30%",
          },
          {
              label: "Increase in Fleet Utilisation",
              value: "25%",
          },
          {
              label: "Distribution Centres",
              value: "12",
          },
      ],
      tech: ["React", "React Native", "FastAPI", "PostgreSQL", "GPS & Maps APIs"],
      accent: "#0F766E",
    },
  },
];
